/**
 * EquiposPage (rc104) — inventario de comodatos (Operación).
 *
 * Tarjetas: Pendientes de recolección (la que importa — destacada),
 * En campo (comodatos vivos), Recuperados, Propiedad del cliente.
 * Vistas: Pendientes (default, ordenados del más viejo) / En campo /
 * Recuperados. Acción "Recuperado ✓" reusa el endpoint F92 (auditado).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, PackageCheck, MapPin, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { cn, formatMXN } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import { useEmpresasMap } from "@/features/empresas/useEmpresasMap";
import type { ApiError } from "@/lib/api";

import {
  useEquiposResumen, useEquiposList, useMarcarRecuperado,
  type EquipoItem, type EquiposVista,
} from "./api";

function diasDesde(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function EquiposPage() {
  const [empresaId, setEmpresaId] = useState<number | null>(null);
  const [vista, setVista] = useState<EquiposVista>("pendientes");
  const [marcando, setMarcando] = useState<EquipoItem | null>(null);

  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRecuperar = hasPermission("contratos.equipo_recuperar");

  const { empresas } = useEmpresasMap();
  const { data: resumen } = useEquiposResumen(empresaId);
  const { data: lista, isLoading } = useEquiposList(vista, empresaId);
  const items = lista?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package size={16} /> Equipos
          </h2>
          <p className="text-xs text-muted-foreground">
            Inventario de comodatos: qué equipo nuestro está en campo y cuál
            falta recoger tras cancelaciones.
          </p>
        </div>
        <div>
          <Label className="text-[10px]">Empresa</Label>
          <Select
            value={empresaId === null ? "" : String(empresaId)}
            onChange={(e) => setEmpresaId(e.target.value === "" ? null : Number(e.target.value))}
            className="text-xs min-w-[170px]"
          >
            <option value="">Todas</option>
            {empresas.map((em) => (
              <option key={em.id} value={em.id}>{em.nombre}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Tarjetas resumen */}
      {resumen && (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          <Card
            className={cn(
              "halo-orbit rounded-xl p-3 shadow-sm transition-all cursor-pointer",
              vista === "pendientes"
                ? "border-danger/50 shadow-md"
                : "hover:border-electric-border hover:shadow-md",
            )}
            onClick={() => setVista("pendientes")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Pendientes de recolección
              </div>
              <AlertCircle size={12} className="text-danger" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-danger">
              {resumen.pendientes_recoleccion}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              cancelados en comodato sin recoger
            </div>
          </Card>

          <Card
            className={cn(
              "halo-orbit rounded-xl p-3 shadow-sm transition-all cursor-pointer",
              vista === "campo"
                ? "border-electric shadow-md"
                : "hover:border-electric-border hover:shadow-md",
            )}
            onClick={() => setVista("campo")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                En campo (comodato)
              </div>
              <Package size={12} className="text-electric" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-electric">
              {resumen.comodato_en_campo}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              equipo nuestro en contratos vivos
            </div>
          </Card>

          <Card
            className={cn(
              "halo-orbit rounded-xl p-3 shadow-sm transition-all cursor-pointer",
              vista === "recuperados"
                ? "border-electric shadow-md"
                : "hover:border-electric-border hover:shadow-md",
            )}
            onClick={() => setVista("recuperados")}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Recuperados
              </div>
              <PackageCheck size={12} className="text-success" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight text-success">
              {resumen.recuperados}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              histórico de recolecciones
            </div>
          </Card>

          <Card className="halo-orbit rounded-xl p-3 shadow-sm">
            <div className="flex items-center justify-between mb-1.5">
              <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Propiedad del cliente
              </div>
              <CheckCircle2 size={12} className="text-muted-foreground" />
            </div>
            <div className="font-mono text-2xl font-semibold tabular-nums tracking-tight">
              {resumen.propiedad_cliente}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mt-1">
              contratos vivos sin equipo nuestro
            </div>
          </Card>
        </div>
      )}

      {/* Lista */}
      <Card className="rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-3">
          {vista === "pendientes" && `Pendientes de recolección (${items.length}) — los más viejos primero`}
          {vista === "campo" && `Comodatos en campo (${items.length})`}
          {vista === "recuperados" && `Recuperados (${items.length})`}
        </h3>
        {isLoading ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin" /> cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border bg-muted/20 p-4 text-center text-xs text-emerald-600 dark:text-emerald-400">
            {vista === "pendientes"
              ? "Sin equipos pendientes de recolección."
              : "Sin registros en esta vista."}
          </div>
        ) : (
          <div className="rounded-md border divide-y overflow-x-auto">
            {items.map((it) => {
              const dias = vista === "pendientes"
                ? diasDesde(it.fecha_cancelacion)
                : null;
              return (
                <div key={it.contrato_id}
                  className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-muted/20">
                  <Link to={`/contratos/${it.contrato_id}`}
                    className="font-mono text-primary hover:underline shrink-0">
                    {it.codigo}
                  </Link>
                  <span className="flex-1 min-w-0 truncate">{it.cliente_nombre}</span>
                  <span className="hidden md:block max-w-[180px] truncate text-muted-foreground"
                    title={it.equipo_descripcion ?? undefined}>
                    {it.equipo_descripcion || "equipo s/desc"}
                  </span>
                  {it.mac && (
                    <span className="hidden lg:block font-mono text-[10px] text-muted-foreground shrink-0">
                      {it.mac}
                    </span>
                  )}
                  {vista === "pendientes" && dias !== null && (
                    <span className={cn(
                      "font-mono text-[10px] shrink-0",
                      dias > 90 ? "text-danger" : dias > 30 ? "text-warn" : "text-muted-foreground",
                    )}>
                      {dias}d
                    </span>
                  )}
                  {vista === "recuperados" && (
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                      {it.equipo_recuperado_at?.slice(0, 10)}
                      {it.recuperado_por_nombre ? ` · ${it.recuperado_por_nombre}` : ""}
                    </span>
                  )}
                  {it.lat != null && it.lng != null && (
                    <a href={`https://maps.google.com/?q=${it.lat},${it.lng}`}
                      target="_blank" rel="noopener noreferrer"
                      title={it.ubicacion_referencia ?? "Abrir en Google Maps"}
                      className="text-primary shrink-0">
                      <MapPin size={13} />
                    </a>
                  )}
                  {vista === "pendientes" && canRecuperar && (
                    <Button size="sm" variant="outline" className="shrink-0"
                      onClick={() => setMarcando(it)}>
                      <PackageCheck size={12} className="mr-1" /> Recuperado
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {marcando && (
        <MarcarRecuperadoDialog item={marcando} onClose={() => setMarcando(null)} />
      )}
    </div>
  );
}

// ─── Dialog de confirmación con notas ────────────────────────────────────
function MarcarRecuperadoDialog({
  item, onClose,
}: { item: EquipoItem; onClose: () => void }) {
  const [notas, setNotas] = useState("");
  const mut = useMarcarRecuperado();

  function submit() {
    mut.mutate(
      { contratoId: item.contrato_id, notas: notas.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(`Equipo de ${item.codigo} marcado como recuperado`, {
            description: "Registrado en auditoría.",
          });
          onClose();
        },
        onError: (e) =>
          toast.error("No se pudo marcar", { description: (e as ApiError).message }),
      },
    );
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck size={16} className="text-success" /> Marcar equipo recuperado
          </DialogTitle>
          <DialogDescription>
            {item.codigo} · {item.cliente_nombre}
            {item.equipo_descripcion ? ` · ${item.equipo_descripcion}` : ""}
            {Number(item.equipo_costo) > 0 ? ` · ${formatMXN(Number(item.equipo_costo))}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Notas (opcional)</Label>
          <Input value={notas} onChange={(e) => setNotas(e.target.value)}
            placeholder="Antena recogida completa, con fuente…" maxLength={500} autoFocus />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button type="button" size="sm" disabled={mut.isPending} onClick={submit}>
            {mut.isPending ? "Marcando…" : "Confirmar recolección"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
